/*
  <shintop-carousel> — the enhancement layer of a CSS scroll-snap row.

  The row scrolls natively without this element. It adds the prev/next arrows (revealed only
  when the track overflows, paging by one visible width), the mouse drag on the cards, and the
  rail: a progress fill whose leading edge can be dragged, clicked or keyed (`bindRail`).
  No dependencies.
*/
if (!customElements.get('shintop-carousel')) {
  customElements.define(
    'shintop-carousel',
    class ShintopCarousel extends HTMLElement {
      static count = 0;

      connectedCallback() {
        this.nav = this.querySelector('.shintop-carousel__nav');
        this.prev = this.querySelector('.shintop-carousel__arrow--prev');
        this.next = this.querySelector('.shintop-carousel__arrow--next');
        this.rail = this.querySelector('.shintop-carousel__rail');
        this.railFill = this.querySelector('.shintop-carousel__rail-fill');
        if (!this.nav) return;

        this.prev?.addEventListener('click', () => this.page(-1));
        this.next?.addEventListener('click', () => this.page(1));
        this.resizeObserver = new ResizeObserver(() => this.queueUpdate());
        this.bindRail();

        /*
          A TRACK SOMEONE ELSE RENDERS LATER (2026-09-29, Reviews band). Judge.me builds its review
          list in the browser after this element connects, so there is no track to find yet.
          `data-track` names it; the element watches its own subtree, takes the list as its track
          when it appears (and gives it the track class, so the shared track CSS applies), and
          binds again if the app replaces the list — Judge.me re-renders it on sort and on page.
        */
        const late = this.dataset.track;
        const own = this.querySelector('.shintop-carousel__track');
        if (own || !late) {
          if (own) this.bindTrack(own);
          return;
        }
        const adopt = () => {
          const found = this.querySelector(late);
          if (!found) return;
          if (!found.classList.contains('shintop-carousel__track')) found.classList.add('shintop-carousel__track');
          if (found !== this.track) this.bindTrack(found);
        };
        this.lateObserver = new MutationObserver(adopt);
        this.lateObserver.observe(this, { childList: true, subtree: true });
        adopt();
      }

      bindTrack(track) {
        if (this.track) this.resizeObserver.unobserve(this.track);
        this.track = track;
        if (this.rail) {
          if (!track.id) track.id = `shintop-carousel-track-${++ShintopCarousel.count}`;
          this.rail.setAttribute('aria-controls', track.id);
        }
        this.bindDrag();
        this.track.addEventListener('scroll', () => this.queueUpdate(), { passive: true });
        this.resizeObserver.observe(this.track);
        this.queueUpdate();
      }

      /*
        GRAB AND DRAG THE ROW — the mouse affordance (2026-09-06). Naoya: "if there was
        trackpad, you can navigate to the right, but what if there is no trackpad and using
        mouse. is there any way to scroll? without using that button on top right?"

        Measured before building: the track is `overflow-x: auto` with `scrollbar-width: none`
        (scoped by `:has(.shintop-carousel__rail)`), so a mouse user's only DISCOVERABLE path
        really was the arrows. Shift+wheel and Tab both work and neither announces itself.

        WHY NOT JUST PUT THE SCROLLBAR BACK: the rail replaced it deliberately on 2026-08-16 —
        drawn together they are two indicators of the same thing two pixels apart, which reads
        as a rendering fault — and the rail is a PROGRESS bar, not a thumb, which is what Naoya
        asked for. Dragging the cards themselves adds no second indicator and no new UI at all,
        so it answers the question without reopening either decision.

        Three things it must not break, each handled:
        - A CLICK MUST STILL BE A CLICK. Nothing happens until the pointer has travelled
          DRAG_SLOP; only then does it become a drag, and only then is the click that follows
          swallowed — captured on the way down so a card's own <a> never sees it.
        - TOUCH IS ALREADY PERFECT and is left completely alone: `pointerType === 'mouse'`
          only. Hijacking touch would replace momentum scrolling with a worse copy of it.
        - SCROLL SNAP FIGHTS A DRAG. `scroll-snap-type` is suspended for the duration and
          restored on release, which is also what lets the row settle onto a card afterwards.
      */
      bindDrag() {
        const DRAG_SLOP = 5;
        let startX = 0;
        let startScroll = 0;
        let pressed = false;
        let dragging = false;
        let justDragged = false;

        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          if (this.track.scrollWidth <= this.track.clientWidth) return;
          pressed = true;
          dragging = false;
          startX = event.clientX;
          startScroll = this.track.scrollLeft;
        });

        this.track.addEventListener('pointermove', (event) => {
          if (!pressed) return;
          // A press released OUTSIDE the row before the slop was crossed never captured the
          // pointer, so its pointerup went to whatever was under the cursor and this track never
          // heard it. Without this, the next buttonless hover over the row "dragged" it (S03-020,
          // theme audit 2026-10-14: 0 → 300px with no button held, the dragging class stuck on).
          if (!(event.buttons & 1)) {
            release(event);
            return;
          }
          const dx = event.clientX - startX;
          if (!dragging) {
            if (Math.abs(dx) < DRAG_SLOP) return;
            dragging = true;
            this.classList.add('shintop-carousel--dragging');
            // Suspending snap is what makes the drag track the cursor 1:1 instead of
            // fighting it back to the nearest card on every frame.
            this.track.style.scrollSnapType = 'none';
            // Guarded: capture throws if the pointer is no longer active (and in synthetic
            // test events there is no active pointer at all). Losing capture costs a drag
            // that stops at the row's edge; throwing here would cost the drag entirely.
            try {
              this.track.setPointerCapture(event.pointerId);
            } catch (_) {
              /* no capture — the drag still tracks while the pointer stays over the row */
            }
          }
          this.track.scrollLeft = startScroll - dx;
        });

        const release = (event) => {
          if (!pressed) return;
          pressed = false;
          if (!dragging) return;
          this.classList.remove('shintop-carousel--dragging');
          this.track.style.removeProperty('scroll-snap-type');
          if (this.track.hasPointerCapture?.(event.pointerId)) {
            this.track.releasePointerCapture(event.pointerId);
          }
          // Swallow the click this drag is about to produce on whichever card the pointer
          // happens to be over. A FLAG, not a one-shot listener: `{ once: true }` only
          // removes itself if a click actually arrives, and a drag released outside the row
          // produces none — leaving the trap armed to eat the shopper's NEXT real click on a
          // product. The flag clears on its own either way.
          justDragged = true;
          setTimeout(() => {
            justDragged = false;
          }, 0);
        };

        this.track.addEventListener('click', (event) => {
          if (!justDragged) return;
          event.preventDefault();
          event.stopPropagation();
        }, { capture: true });

        this.track.addEventListener('pointerup', release);
        this.track.addEventListener('pointercancel', release);

        /*
          NATIVE DRAG-AND-DROP IS WHAT BROKE THE DRAG OVER A CARD (2026-09-06). Naoya: "being
          able to drag and scroll with just using mouse is good, but when hovering product card,
          it cannot, and this experience is sort of poor."

          Every card is an <a> wrapping an <img>, and both are `draggable` by default. Press and
          move on one and the browser starts ITS OWN drag — a ghost thumbnail follows the cursor
          and the pointer stream is cancelled — so the row froze exactly where a shopper is most
          likely to grab it. Pressing the gap between cards worked, which is why it read as
          intermittent rather than broken.

          Worth stating how this was found, because the rig said the opposite: synthetic
          PointerEvents dispatched at the card DID scroll the track (measured +150px). Native
          drag is a browser behaviour no synthetic event reproduces, so the probe passed on a
          path that fails for a real mouse. The tell was in the DOM, not the events —
          `img.draggable === true` and a `dragstart` that nothing prevented.
        */
        this.track.addEventListener('dragstart', (event) => event.preventDefault());
      }

      /*
        THE RAIL STEERS NOW (Naoya, Quick Calls 2026-10-17: "make it a draggable scrollbar?" →
        yes; "on phones, make the row's scroll bar easier to see?" → yes). Four cold-readers had
        taken the bar for "a stuck loading bar" — a thing that only reports reads as broken.

        It is still the PROGRESS bar he asked for on 2026-08-17 — the fill grows to the last
        card — and what is dragged is the fill's leading edge, the knob CSS draws there: a
        scrubber, the shape a video bar has taught everyone. The pointer maps straight onto
        the fill, `(scrollLeft + clientWidth) / scrollWidth`, so the knob stays under the
        finger; a press anywhere else on the rail jumps the edge there first, then drags.

        Upgraded here rather than in the markup: eight sections draw the rail and none of them
        has to change for it to become a control, and without this script it is the
        `aria-hidden` decoration it always was. With it, a `scrollbar` — focusable, named by
        the markup's `data-label`, ←/→ a card, Page Up/Down a screen, Home/End.

        Every pointer type, unlike the card drag: the card drag leaves touch alone because a
        finger on a card already scrolls natively, but a finger on a 6px bar does nothing,
        so the rail takes it (`touch-action: none` in the CSS keeps the page from panning).
      */
      bindRail() {
        const rail = this.rail;
        if (!rail || !this.railFill) return;
        rail.removeAttribute('aria-hidden');
        rail.setAttribute('role', 'scrollbar');
        rail.setAttribute('aria-orientation', 'horizontal');
        rail.setAttribute('aria-valuemin', '0');
        rail.setAttribute('aria-valuemax', '100');
        if (rail.dataset.label) rail.setAttribute('aria-label', rail.dataset.label);
        rail.tabIndex = 0;

        let grab = null;
        const scrubTo = (clientX) => {
          const box = rail.getBoundingClientRect();
          const { scrollWidth, clientWidth } = this.track;
          const reached = (clientX + grab - box.left) / box.width;
          this.track.scrollLeft = reached * scrollWidth - clientWidth;
        };
        const end = (event) => {
          if (grab === null) return;
          grab = null;
          this.classList.remove('shintop-carousel--scrubbing');
          this.track.style.removeProperty('scroll-snap-type');
          this.track.style.removeProperty('scroll-behavior');
          if (rail.hasPointerCapture?.(event.pointerId)) rail.releasePointerCapture(event.pointerId);
        };

        rail.addEventListener('pointerdown', (event) => {
          if (!this.track || event.button !== 0) return;
          event.preventDefault();
          const edge = this.railFill.getBoundingClientRect().right;
          const knob = Math.max(this.railFill.offsetHeight * 2, 12);
          // On the knob: keep the offset so it does not jump. Anywhere else: jump to the press.
          grab = Math.abs(edge - event.clientX) <= knob ? edge - event.clientX : 0;
          this.classList.add('shintop-carousel--scrubbing');
          // Snap and smooth scrolling would both pull the row away from the pointer each frame.
          this.track.style.scrollSnapType = 'none';
          this.track.style.scrollBehavior = 'auto';
          try {
            rail.setPointerCapture(event.pointerId);
          } catch (_) {
            /* no capture — the scrub still tracks while the pointer stays over the rail */
          }
          scrubTo(event.clientX);
        });
        rail.addEventListener('pointermove', (event) => {
          if (grab === null) return;
          if (!(event.buttons & 1)) {
            end(event);
            return;
          }
          scrubTo(event.clientX);
        });
        rail.addEventListener('pointerup', end);
        rail.addEventListener('pointercancel', end);
        rail.addEventListener('lostpointercapture', end);

        rail.addEventListener('keydown', (event) => {
          if (!this.track) return;
          const overflow = this.track.scrollWidth - this.track.clientWidth;
          const card = this.track.firstElementChild;
          const step = card ? card.offsetWidth + (parseFloat(getComputedStyle(this.track).columnGap) || 0) : 0;
          const moves = {
            ArrowLeft: -step,
            ArrowRight: step,
            PageUp: -this.track.clientWidth,
            PageDown: this.track.clientWidth,
          };
          if (event.key in moves) {
            this.track.scrollBy({ left: moves[event.key] });
          } else if (event.key === 'Home' || event.key === 'End') {
            this.track.scrollTo({ left: event.key === 'Home' ? 0 : overflow });
          } else {
            return;
          }
          event.preventDefault();
        });
      }

      disconnectedCallback() {
        this.resizeObserver?.disconnect();
        this.lateObserver?.disconnect();
      }

      /*
        NO `behavior` HERE (S06-019, theme audit 2026-10-14). An explicit 'smooth' overrode the
        track's own `scroll-behavior`, which is `smooth` and drops to `auto` under
        prefers-reduced-motion — so the arrows animated for exactly the shoppers who asked them
        not to. Left out, the call takes the track's computed value: every track measured
        `smooth` without the preference.

        An arrow at its end is `aria-disabled`, not `disabled` (S06-013): disabling the focused
        button threw focus to <body> the moment the row reached its end. It stays focusable and
        does nothing.
      */
      page(direction) {
        const arrow = direction < 0 ? this.prev : this.next;
        if (arrow?.getAttribute('aria-disabled') === 'true') return;
        this.track?.scrollBy({ left: direction * this.track.clientWidth });
      }

      queueUpdate() {
        if (this.raf) return;
        this.raf = requestAnimationFrame(() => {
          this.raf = null;
          this.update();
        });
      }

      update() {
        if (!this.track) return;
        const overflow = this.track.scrollWidth - this.track.clientWidth;
        const scrollable = overflow >= 8;

        this.nav.hidden = !scrollable;
        if (this.rail) this.rail.hidden = !scrollable;

        /*
          THE RAIL FILLS, IT DOES NOT SLIDE. Naoya, 2026-08-17: "right now the same width line
          is moving to the right as you scroll, but i want the length to be longer while
          reaching to the edge of scrolling. so when you reach at the last product, it is a
          full width line."

          So the fill measures the RIGHT EDGE of what you have seen — `(scrollLeft +
          clientWidth) / scrollWidth` — rather than a window sliding along a trough. It starts
          at the share of the row that is on screen (never at zero, because you HAVE already
          seen that much) and reaches exactly 100% on the last card. That is a progress bar,
          which is what he asked for; the sliding window was a scrollbar thumb, which reports
          the same information in a shape people read as "drag me".

          Still proportional, never counted: a breakpoint that changes the card count, a card
          dropped by its own guard, or a scroll resting between two cards all stay honest,
          where counting cards would need a second source of truth for the CSS to disagree
          with.

          Width rather than transform now. A width animates off the compositor, so this is the
          one place the rail costs a layout per frame — but it is a 3px bar in its own
          `overflow: hidden` parent with nothing beside it to reflow, and the alternative
          (scaleX on a full-width bar) blurs the rounded end caps at small scales.
        */
        if (this.railFill && scrollable) {
          const seen = (this.track.scrollLeft + this.track.clientWidth) / this.track.scrollWidth;
          this.railFill.style.width = `${(Math.min(seen, 1) * 100).toFixed(3)}%`;
          if (this.rail.getAttribute('role') === 'scrollbar') {
            this.rail.setAttribute('aria-valuenow', String(Math.round((this.track.scrollLeft / overflow) * 100)));
          }
        }

        if (!scrollable) return;
        this.prev?.setAttribute('aria-disabled', String(this.track.scrollLeft < 8));
        this.next?.setAttribute('aria-disabled', String(this.track.scrollLeft > overflow - 8));
      }
    },
  );
}
